/**
 * Session state and activity log upload with always-SQLite-first, renderer retry, and main handoff.
 * Uses dynamic import of session store to avoid circular dependency.
 */

import {
  hasSessionPreloadAPI,
  checkNetworkStatus,
  saveSessionToDB,
  markSessionUploaded,
  enqueueSessionSync,
  triggerSync
} from '@renderer/utils/pipeline/bridgeApi'
import type { SaveSessionParams, ActivityLogLevel } from '@renderer/types/outputPipeline.types'
import { isDigitalMode } from '@renderer/lib/runtime'
import { isSimulationActive } from '@renderer/lib/simulation'

const MAX_RETRIES = 3
const RETRY_DELAYS_MS = [1000, 2000, 4000]

// Per-session in-flight guard: prevents concurrent POSTs for the same session.
// Subsequent callers wait for the first request to finish rather than firing a duplicate.
const inflightUploads = new Map<string, Promise<void>>()

// Signature of the last successfully-POSTed payload per session. If the next
// upload would be byte-for-byte identical we skip the round-trip. The
// signature includes every field that matters for the backend write —
// isCompleted, transaction.status, photoPublication, extraPrintCopies,
// extraPrintAmount, etc. — so toggling any "checkpoint" field automatically
// changes the signature and the POST goes through.
const lastSuccessfulPayloadSig = new Map<string, string>()

/** Stable JSON serialization with sorted keys (recursive). */
function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value)
  }
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`
  }
  const obj = value as Record<string, unknown>
  const keys = Object.keys(obj).sort()
  return `{${keys
    .map((k) => `${JSON.stringify(k)}:${stableStringify(obj[k])}`)
    .join(',')}}`
}

function computePayloadSignature(sessionData: Record<string, unknown>): string {
  // Strip volatile fields that don't affect the stored record but DO change
  // every call (Date.now in timestamps would be a worse offender, but those
  // are inside the dated fields the backend cares about anyway).
  const { token: _token, deviceId: _deviceId, ...rest } = sessionData as {
    token?: unknown
    deviceId?: unknown
  } & Record<string, unknown>
  return stableStringify(rest)
}

/** Build session payload for API/SQLite from store state (merged with snapshot). */
async function buildSessionData(
  sessionId: string,
  stateSnapshot?: Record<string, unknown> | null
): Promise<SaveSessionParams> {
  const { useSessionStateStore } = await import('@renderer/stores/sessionState')
  const { useAuthStore } = await import('@renderer/stores/authStore')
  const { useSettingsStore } = await import('@renderer/stores/settingsStore')
  const { useConfigStore } = await import('@renderer/stores/configState')
  const { useHubStore } = await import('@renderer/stores/hubStore')

  const get = (): ReturnType<typeof useSessionStateStore.getState> =>
    useSessionStateStore.getState()
  const currentState =
    stateSnapshot != null && Object.keys(stateSnapshot).length > 0
      ? { ...get(), ...stateSnapshot }
      : get()

  const rawGalleryKey = (currentState as { galleryKey?: string; gallery?: { id?: string } }).galleryKey || (currentState as { gallery?: { id?: string } }).gallery?.id || ''
  const fullGalleryKey =
    rawGalleryKey && String(rawGalleryKey).startsWith('gallery/')
      ? rawGalleryKey
      : rawGalleryKey && (currentState as { userId?: string; projectId?: string }).userId && (currentState as { projectId?: string }).projectId
        ? `gallery/${(currentState as { userId: string }).userId}/${(currentState as { projectId: string }).projectId}/${rawGalleryKey}`
        : rawGalleryKey
  const backendGalleryKey =
    fullGalleryKey && String(fullGalleryKey).startsWith('gallery/')
      ? String(fullGalleryKey).split('/').pop() || rawGalleryKey
      : rawGalleryKey

  const sessionData = currentState as unknown as {
    projectId: string
    userId: string
    eventId?: string
    timeline: Record<string, unknown>
    userPreferences: Record<string, unknown>
    userConsent: Record<string, unknown>
    devices: Record<string, unknown>
    transaction: Record<string, unknown>
    gallery: Record<string, unknown>
    appSettingsTelemetryId?: string
  }

  // Guarantee a Mongo ObjectId reaches POST /api/sessions. In digital mode the
  // route param is a slug; sessionState.projectId should already be the resolved
  // config._id (set in Welcome), but guard here too so a slug can never trigger a
  // Cast-to-ObjectId 500 server-side. Kiosk: both are the same ObjectId (no-op).
  const OBJECT_ID_RE = /^[a-f0-9]{24}$/i
  const configId = useConfigStore.getState().config?._id
  const resolvedProjectId =
    typeof sessionData.projectId === 'string' && OBJECT_ID_RE.test(sessionData.projectId)
      ? sessionData.projectId
      : typeof configId === 'string' && OBJECT_ID_RE.test(configId)
        ? configId
        : sessionData.projectId

  return {
    id: sessionId,
    projectId: resolvedProjectId,
    userId: sessionData.userId,
    eventId: sessionData.eventId,
    userEmail: useAuthStore.getState().user?.email,
    token: useSettingsStore.getState().activationCode,
    deviceId: useSettingsStore.getState().deviceId,
    // Attribute a member session to the hub it was entered from. Sessions stay
    // keyed by the MEMBER projectId (analytics/withdrawals per mode for free);
    // this only enables hub-level rollups. Studio's Session schema declares it.
    ...((): { hubId?: string } => {
      const hub = useHubStore.getState()
      return hub.hubId && hub.memberProjectId === resolvedProjectId ? { hubId: hub.hubId } : {}
    })(),
    timeline: sessionData.timeline as Record<string, unknown>,
    userPreferences: sessionData.userPreferences as Record<string, unknown>,
    userConsent: sessionData.userConsent as Record<string, unknown>,
    devices: sessionData.devices as Record<string, unknown>,
    transaction: sessionData.transaction as Record<string, unknown>,
    gallery: sessionData.gallery as Record<string, unknown>,
    galleryKey: backendGalleryKey,
    appSettingsTelemetryId: sessionData.appSettingsTelemetryId,
    // Tag digital web-booth sessions so kiosk vs digital is queryable server-side.
    ...(isDigitalMode() ? { source: 'digital' } : {})
  }
}

/**
 * Refactored session upload: 1) Always persist to SQLite first (when preload exists),
 * 2) POST /api/sessions with 3 fast retries, 3) On success mark uploaded, 4) On failure hand off to main.
 *
 * Uses an in-flight guard so concurrent calls for the same session wait rather than
 * firing duplicate POSTs.
 */
export async function uploadSessionState(
  sessionId?: string,
  stateSnapshot?: Record<string, unknown> | null,
  options?: { force?: boolean }
): Promise<void> {
  // Sim Mode is read-only: simulated sessions never persist (no SQLite, no API).
  if (isSimulationActive()) {
    console.warn('[stateUploader] Skipping session upload — simulation mode is active')
    return
  }
  const { useSessionStateStore } = await import('@renderer/stores/sessionState')
  const get = (): ReturnType<typeof useSessionStateStore.getState> =>
    useSessionStateStore.getState()
  const currentState =
    stateSnapshot != null && Object.keys(stateSnapshot).length > 0
      ? { ...get(), ...stateSnapshot }
      : get()
  const id = sessionId || (currentState as { id?: string }).id
  if (!id) {
    // A silent return here is what hid the karcis booths from the backend for
    // their entire deployment: a custom path that renders its whole flow in one
    // component never ran a `startSession`, so the store's id stayed '' and every
    // upload no-opped — no sessions, and (because activity logs are keyed by
    // session) no logs either. Anything calling this without a session is a bug
    // in the caller, so say so instead of disappearing.
    console.error(
      '[stateUploader] Upload skipped — no session id. The caller must create a session (setSessionState({ id })) before uploading.'
    )
    return
  }

  // If an upload for this session is already in-flight, wait for it instead of duplicating
  const existing = inflightUploads.get(id)
  if (existing) {
    await existing
    return
  }

  const promise = doUploadSessionState(id, stateSnapshot, options?.force === true)
  inflightUploads.set(id, promise)
  try {
    await promise
  } finally {
    inflightUploads.delete(id)
  }
}

async function doUploadSessionState(
  id: string,
  stateSnapshot?: Record<string, unknown> | null,
  force: boolean = false
): Promise<void> {
  const sessionData = await buildSessionData(id, stateSnapshot)
  const hasPreload = hasSessionPreloadAPI()

  if (hasPreload) {
    const saved = await saveSessionToDB(sessionData)
    if (!saved.success) {
      console.warn('[stateUploader] saveSessionToDB failed:', saved.error)
    }
  }

  // Skip the API round-trip when the payload hasn't changed since the last
  // successful POST. SQLite still updated above, and finalizeSession passes
  // force=true so the "session complete" upload always goes through.
  const signature = computePayloadSignature(sessionData as unknown as Record<string, unknown>)
  if (!force && lastSuccessfulPayloadSig.get(id) === signature) {
    console.log(`[stateUploader] Skipping POST for ${id} — payload unchanged since last success`)
    return
  }

  let isOnline = await checkNetworkStatus()
  let lastError: Error | null = null
  if (isOnline) {
    try {
      const { useCreditsStore } = await import('@renderer/stores/creditsStore')
      let wait = 0
      while (useCreditsStore.getState().isLoading && wait < 5000) {
        await new Promise((r) => setTimeout(r, 100))
        wait += 100
      }
    } catch {
      // ignore
    }

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      isOnline = await checkNetworkStatus()
      if (!isOnline) break
      try {
        const body: Record<string, unknown> = { ...sessionData }
        const response = await fetch(`${process.env.VITE_API_URL}/api/sessions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          cache: 'no-cache',
          credentials: 'include',
          body: JSON.stringify(body)
        })
        if (!response.ok) {
          const text = await response.text()
          const isDeviceMismatch = response.status === 403 && text.includes('Device not authorized')
          const isNonTransient =
            response.status === 403 &&
            (text.includes('Insufficient credits') || text.includes('Forbidden') || isDeviceMismatch)
          if (isNonTransient) {
            console.error(
              '[stateUploader] Non-transient 403, not retrying:',
              text
            )
            if (isDeviceMismatch) {
              import('@renderer/stores/creditsStore').then(({ useCreditsStore }) => {
                void useCreditsStore.getState().fetchSubscriptionAndCredits()
              })
            }
            throw new Error(`NON_TRANSIENT:${response.status} ${text}`)
          }
          throw new Error(`${response.status} ${response.statusText} - ${text}`)
        }
        if (hasPreload) {
          await markSessionUploaded(id)
        }
        lastSuccessfulPayloadSig.set(id, signature)
        return
      } catch (err) {
        lastError = err instanceof Error ? err : new Error(String(err))
        const isNonTransient = lastError.message.startsWith('NON_TRANSIENT:')
        if (isNonTransient) {
          break
        }
        console.error('[stateUploader] Error uploading session state:', lastError.message)
        if (attempt < MAX_RETRIES) {
          await new Promise((r) => setTimeout(r, RETRY_DELAYS_MS[attempt]))
        }
      }
    }
  }

  if (lastError?.message.startsWith('NON_TRANSIENT:')) {
    throw lastError
  }

  if (hasPreload) {
    const enqueued = await enqueueSessionSync(id)
    if (enqueued.success) {
      await triggerSync()
    }
  }
}

/**
 * Log activity: delegates to the session store which handles both API upload
 * and SQLite persistence. Do NOT also call saveActivityLogsToDB here — that
 * would create a duplicate entry in SQLite (and thus a duplicate API upload
 * via the syncManager).
 */
export async function logActivity(
  sessionId: string,
  message: string,
  _level: ActivityLogLevel = 'info',
  details?: Record<string, unknown>,
  page?: string
): Promise<void> {
  console.log('logActivity', sessionId, message, _level, details, page)
  const { useSessionStateStore } = await import('@renderer/stores/sessionState')
  useSessionStateStore.getState().logActivity(message, details, page, sessionId)
}

export async function triggerSyncNow(): Promise<void> {
  await triggerSync()
}

/**
 * Mark session complete, upload, and trigger sync (e.g. for Result page).
 * Forces the POST through even if the payload signature hasn't changed —
 * finalization is a checkpoint that must reach the server (credit ledger,
 * receipt emails, gallery indexing all depend on it).
 */
export async function finalizeSession(sessionId: string): Promise<void> {
  await uploadSessionState(sessionId, null, { force: true })
  await triggerSync()
}
